/**
 * 작성자 : 박승호
 * 날짜 : 2025.04.01
 * 경로 : 출역 관리 - 개인별 월 출역현황 (사내)
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { IModal, ITabObject } from 'customTypes';
import { pageInfoState, themeState, userState } from '../../atoms';
import { MONTH_LIST, YEAR_LIST } from '../../_constants';
import { dark, light } from '../../assets/styles/theme';
import { SearchOptions } from '../../assets/styles/SearchOptions';
import { ContentsContainerRoot } from '../../assets/styles/ContentsContainerRoot';
import { LoadingModalBackground } from '../../assets/styles/Modal';
import { BtnGhost, InitBtn } from '../../components/Button';
import Tab from '../../components/Tab';
import Portal from '../../components/Portal';
import Toggle from '../../components/Toggle';
import AttendDailyPersonalListModal from '../../components/Modal/AttendDailyPersonalListModal';
import { useSetAuth } from '../../utils/useSetAuth';
import { trimObject } from '../../utils/trimObject';
import { todayYYYYMMDD } from '../../utils/formatDate';
import { isMobile } from '../../utils/checkMobile';
import useOnKeydownF9 from '../../utils/useOnKeydownF9';
import { logPost } from '../../services/log';
import i18n from '../../translation/i18n';
import { apiGet } from '../../services/_common';
import ShortcutButton from '../../components/button/ShortcutButton';
import SearchFilterModal from '../../components/Modal/SearchFilterModal';
import Input from '../../components/Input';
import GroupChkModal from '../../components/Modal/GroupChkModal';

const Root = styled(ContentsContainerRoot)`
  display: flex;
  flex-direction: column;
  .textBtnGroup {
    gap: 0.5rem;
    align-items: center;
    > button {
      height: 2.5rem;
      padding: 0 0.75rem;
      font-size: 0.875rem;
      line-height: 1;
    }
  }
  .btn {
    button {
      height: 2.5rem;
      padding: 0 0.75rem;
      font-size: 0.875rem;
    }
  }
  > div:nth-child(2) {
    overflow: auto;

    @media screen and (max-width: 1024px) {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  }
  .searchData {
    .mobile-hidden {
      @media screen and (max-width: 768px) {
        display: none;
      }
    }
  }
  .calendarMonthTitle {
    text-align: center;
    margin-top: 1rem;
    color: var(--text-primary);
    gap: 0.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;

    @media screen and (max-width: 1024px) {
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }

    > div {
      width: 3rem;
      height: 3rem;
      border-radius: 5px;
      :hover {
        color: var(--primary);
        cursor: pointer;
      }

      @media screen and (max-width: 768px) {
        width: 2rem;
        height: 2rem;
      }
    }

    .month-title {
      min-width: 15rem;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 1.5rem;
      padding: 0 0.5rem;
      width: fit-content;
      cursor: pointer;

      @media screen and (max-width: 768px) {
        min-width: auto;
        font-size: 1.25rem;
      }
    }
  }

  .calendar {
    display: grid;
    margin: 0.5rem;
    border: 1px solid var(--outline);
    border-radius: 10px;
    overflow: auto;
    grid-template-columns: repeat(7, 1fr);
    gap: 0;

    @media screen and (max-width: 1024px) {
      padding: 0.25rem;
      flex: 1;
      min-height: 0;
      margin-bottom: 0;
      grid-template-rows: auto;
      grid-auto-rows: minmax(0, 1fr);
    }

    .days {
      min-width: 0;
      text-align: center;
      padding: 0.5rem;
      font-weight: 700;
      border-left: 1px solid var(--outline);
      background-color: var(--background);
      font-size: 1rem;

      @media screen and (max-width: 1024px) {
        padding: 0.125rem;
        font-size: 0.6875rem;
        line-height: 1;
      }
    }

    .emptyCells {
      border-left: 1px solid var(--outline);
      border-top: 1px solid var(--outline);
    }

    .date {
      position: relative;
      height: 12rem;
      font-weight: 600;
      border-top: 1px solid var(--outline);
      padding: 0.875rem;
      cursor: pointer;
      display: flex;
      flex-direction: column;

      @media screen and (max-width: 1024px) {
        height: auto;
        min-height: 0;
        padding: 0.0625rem;
      }

      img {
        align-items: center;
        display: flex;
        justify-content: center;
        margin: 0 auto;
        height: calc(100% - 2rem);
        object-fit: contain;

        @media screen and (max-width: 768px) {
          height: calc(100% - 1.5rem);
        }
      }

      > div {
        height: 100%;
        .dateNumber {
          font-size: 1.0625rem;
          font-weight: 500;

          @media screen and (max-width: 1024px) {
            font-size: 0.75rem;
            font-weight: 400;
            line-height: 1;
          }
        }
        > div:first-child {
          height: calc(100% - 0.6rem);
        }
        > div:nth-child(1) > div:nth-child(2) {
          color: var(--text-secondary);
        }
        > div:nth-child(2) {
          color: var(--text-tertiary);
          font-size: 0.875rem;

          @media screen and (max-width: 768px) {
            font-size: 0.75rem;
          }
        }
      }
    }
  }

  .timeBadge {
    display: inline-flex;
    align-items: center;
    gap: 0.125rem;
    border-radius: 999px;
    padding: 0.125rem 0.375rem;
    background-color: var(--tonal-blue);
    color: var(--selected-primary);
    line-height: 1;
    font-size: 0.5625rem;
    white-space: nowrap;

    .material-symbols-rounded {
      font-size: 0.625rem !important;
      color: inherit;
      line-height: 1;
    }
  }

  .timeBadge.in {
    background-color: var(--tonal-blue);
    color: var(--blue-500);
  }

  .timeBadge.out {
    background-color: var(--tonal-amber);
    color: var(--amber-500);
  }

  .timeBadge.same {
    background-color: var(--tonal-blue);
    color: var(--selected-primary);
  }

  .timeBadge.desktop {
    font-size: 0.8125rem;
    padding: 0.25rem 0.5625rem;

    .material-symbols-rounded {
      font-size: 0.9375rem !important;
    }
  }

  .timeBadge.photoOff {
    font-size: 0.75rem;
    padding: 0.1875rem 0.5rem;

    .material-symbols-rounded {
      font-size: 0.875rem !important;
    }
  }

  .timeBadge.desktop.photoOff {
    font-size: 0.8125rem;
    padding: 0.25rem 0.5625rem;

    .material-symbols-rounded {
      font-size: 0.9375rem !important;
    }
  }
`;

const MonthPickerPopupStyle = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-53%);
  background: var(--background);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  z-index: 1000;

  .year-month-selector {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .year-selector {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;

      @media screen and (max-width: 1024px) {
        margin-bottom: 1.125rem;
      }

      select {
        padding: 0.25rem;
        border-radius: 4px;
        border: 1px solid var(--tonal);
        outline: none;

        @media screen and (max-width: 1024px) {
          border-width: 0 1px 1px 1px;
        }

        &:focus {
          border-color: var(--primary);
        }
      }
    }

    .month-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.5rem;

      button {
        color: var(--text-tertiary);
        padding: 0.5rem;
        border: 1px solid var(--tonal);
        border-radius: 4px;
        background-color: transparent;
        cursor: pointer;

        @media screen and (max-width: 768px) {
          height: fit-content;
          padding: 10px 5px;
          font-size: 1rem;
        }

        &:hover {
          border: 1px solid var(--filled-blue-light);
        }

        &.selected {
          background-color: var(--tonal-blue);
        }
      }
    }
  }
`;

interface ISearchOption {
  wName: string;
  wCd: string;
}

const Cattend5 = () => {
  dayjs.locale(i18n.language === 'en-US' ? 'en' : 'ko');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const size = useOutletContext<any>();
  const tabInfo = useRecoilValue(pageInfoState);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(tabInfo.defaultMrCd);
  const toDay = todayYYYYMMDD();
  const userInfo = useRecoilValue(userState);
  const { userMenuList } = userInfo;
  const componentRef = useRef<HTMLDivElement>(null);
  const [searchOption, setSearchOption] = useState<ISearchOption>({ wName: '', wCd: '' });
  const [sjCd, setSjCd] = useState({ type: 'sjCd', sjCd: '', cdName: '' });
  const [aYear, setAYear] = useState({ type: 'aYear', aYear: toDay.substring(0, 4), cdName: toDay.substring(0, 4) });
  const [month, setMonth] = useState({ type: 'month', month: toDay.substring(4, 6), cdName: toDay.substring(4, 6) });
  const [openModal, setOpenModal] = useState<IModal | any>({ status: false, type: '', code: { wCd: '' }, isOverlappingModal: false });
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [workerCandidates, setWorkerCandidates] = useState<any[]>([]);
  const [selectedCandidateKeys, setSelectedCandidateKeys] = useState<string[]>([]);
  const [photoViewYn, setPhotoViewYn] = useState(true);
  const [newTabList, setNewTabList] = useState<ITabObject[]>();
  const theme = useRecoilValue(themeState);
  const [loading, setLoading] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const isMobileView = isMobile();

  const handleMonthChange = async (type: 'prev' | 'next') => {
    let currentYear = parseInt(aYear.aYear, 10);
    let currentMonth = parseInt(month.month, 10);

    if (type === 'prev') {
      currentMonth -= 1;
      if (currentMonth < 1) {
        currentMonth = 12;
        currentYear -= 1;
      }
    } else if (type === 'next') {
      currentMonth += 1;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear += 1;
      }
    }

    const newYear = currentYear.toString();
    const newMonth = currentMonth.toString().padStart(2, '0');

    setMonth({ type: 'month', month: newMonth, cdName: newMonth });
    setAYear({ type: 'aYear', aYear: newYear, cdName: newYear });

    const targetWCd = selectedUser?.wCd || searchOption.wCd;
    if (targetWCd) getMonthlyAttendListAPI(targetWCd, newYear, newMonth);
    else toast.warn(t('근로자를 선택하세요'));
  };

  useEffect(() => {
    const arr: any[] = [];
    userMenuList.map((v: any) => {
      const result = tabInfo?.tabList?.find((el: ITabObject) => el.mtCd === v.mtCd && el.mdCd === v.mdCd && el.mrCd === v.mrCd);
      if (result) {
        arr.push(result);
        setNewTabList(arr);
      }
    });
  }, [tabInfo.tabList]);

  const days = ['일', '월', '화', '수', '목', '금', '토'];
  const getDateArray = (year = aYear.aYear, selectedMonth = month.month) => {
    let startDay = dayjs(`${year}-${selectedMonth}-01`);
    const endDay = startDay.endOf('month');
    const dateArray: any = [];

    while (startDay.isBefore(endDay)) {
      // dateArray.push(startDay.format('DD ddd'));
      dateArray.push({ date: startDay.format('DD'), dayOfWeek: startDay.day(), aImg: '', aTimeMin: '', aTimeMax: '' });
      startDay = startDay.add(1, 'day');
    }
    return dateArray;
  };
  const [dateArray, setDateArray] = useState([]);
  const [firstDayOfMonth, setFirstDayOfMonth] = useState(dayjs(`${aYear.aYear}-${month.month}-01`).day());
  const [totalDays, setTotalDays] = useState(firstDayOfMonth + dateArray.length);
  const [remainingCells, setRemainingCells] = useState(totalDays < 35 ? 35 - totalDays : 42 - totalDays);
  const [monthTitle, setMonthTitle] = useState<string>('');
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [searchParams] = useSearchParams();
  const tabCd = searchParams.get('tabCd');

  // URL의 tabCd 파라미터 변경 감지하여 탭 업데이트
  useEffect(() => {
    if (tabCd && tabCd !== currentTabMrCd) {
      setCurrentTabMrCd(tabCd);
    }
  }, [tabCd]);

  useEffect(() => {
    setDateArray(getDateArray());
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '개인별 월 출역현황(사내)',
      action: '조회',
      etc: ``,
    });
  }, []);

  // 일 출역현황 > 근로자 모달 > '월 출역현황' 버튼으로 진입 시 전달받은 근로자·월로 바로 조회
  useEffect(() => {
    const { wCd, wName, year, month: monthParam } = (location.state as any) || {};
    if (!wCd || !year || !monthParam) return;
    setAYear({ type: 'aYear', aYear: year, cdName: year });
    setMonth({ type: 'month', month: monthParam, cdName: monthParam });
    setSelectedUser({ wCd, wName });
    setSearchOption((prev: ISearchOption) => ({ ...prev, wName, wCd }));
    getMonthlyAttendListAPI(wCd, year, monthParam);
    // 새로고침·뒤로가기 재진입 시 자동 조회가 반복되지 않도록 state 제거
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, []);

  useEffect(() => {
    const result = dayjs(`${aYear.aYear}-${month.month}-01`);
    setMonthTitle(result.format(i18n.language === 'en-US' ? `YYYYMMMM` : `YYYY년 MM월`));
  }, [aYear.aYear, month.month, i18n.language]);

  useEffect(() => {
    if (isF9Pressed) {
      // 근로자검색 모달에서 F9를 눌렀을 때 월출역보고서의 F9가 트리거되지 않도록 처리함
      if (!openModal.status) onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    setTotalDays(firstDayOfMonth + dateArray.length);
  }, [firstDayOfMonth, dateArray]);

  useEffect(() => {
    setRemainingCells(totalDays < 35 ? 35 - totalDays : 42 - totalDays);
  }, [totalDays]);

  // 월출역현황 조회
  const getMonthlyAttendListAPI = async (wCdParam: string, yearParam = aYear.aYear, monthParam = month.month) => {
    const obj = { wCd: wCdParam, aMonth: `${yearParam}${monthParam}` };
    const newDateArray = getDateArray(yearParam, monthParam);

    setFirstDayOfMonth(dayjs(`${yearParam}-${monthParam}-01`).day());
    setDateArray(newDateArray);
    setLoading(true);

    const req = { ...obj, hCd: userInfo.hCd, sCd: userInfo.sCd };
    const res = await apiGet({ path: '/attend/monthDetail', req });

    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      setLoading(false);

      if (data.attendList.length > 0) {
        const updatedDates = newDateArray.map((dateObj: any) => {
          const attendItem = data.attendList.find((attend: any) => attend.aDate.substring(6, 8) === dateObj.date);
          if (attendItem) {
            return {
              ...dateObj,
              aTimeMin: attendItem.aTimeMin,
              aTimeMax: attendItem.aTimeMax,
              aImg: attendItem.aImg,
            };
          }
          return dateObj;
        });
        setDateArray(updatedDates);
      } else {
        toast.info(t('출역데이터가 없습니다.'));
        setDateArray(newDateArray);
      }
    } else {
      setLoading(false);
      // toast.error(t(ERROR));
    }
  };

  const getWorkerListAPI = async () => {
    const newObj = {
      sjCd: sjCd.sjCd,
      wName: searchOption.wName,
    };
    const trimData = trimObject(newObj);
    const req = { ...trimData, hCd: userInfo.hCd, sCd: userInfo.sCd };
    const res = await apiGet({ path: '/worker', req });
    const { statusCode, data } = res.data;
    if (statusCode === 200) {
      return data.workerList || [];
    }
    return [];
  };

  const onSelectWorker = async (worker: any) => {
    setSelectedUser(worker);
    setSearchOption((prev: ISearchOption) => ({ ...prev, wName: worker.wName, wCd: worker.wCd }));
    setWorkerCandidates([]);
    setSelectedCandidateKeys([]);
    if (openModal.type === 'workerCandidateSelect') {
      setOpenModal((prev: any) => ({ ...prev, status: false, type: '' }));
    }
    await getMonthlyAttendListAPI(worker.wCd, aYear.aYear, month.month);
  };

  // 후보 라벨: 이름 (생년월일) - 생년월일이 없으면 이름만
  const formatWorkerCandidateLabel = (worker: any) => (worker.wBdate ? `${worker.wName} (${worker.wBdate})` : worker.wName);

  useEffect(() => {
    if (selectedCandidateKeys.length !== 1) return;
    const selectedWorker = workerCandidates.find((worker: any) => worker.wCd === selectedCandidateKeys[0]);
    if (selectedWorker) {
      onSelectWorker(selectedWorker).catch((error) => {
        console.error(error);
      });
    }
  }, [selectedCandidateKeys]);

  // 검색 버튼 클릭
  const onClickSearch = async () => {
    // 검색 시 입력창 포커스를 해제해 가상 키보드를 내림 (back 버튼 1회 누름이 키패드 닫힘으로 자연스럽게 동작)
    if (isMobileView) (document.activeElement as HTMLElement | null)?.blur?.();
    const keyword = searchOption.wName.trim();
    const normalizeName = (value: string) => value.replace(/\s+/g, '').toLowerCase();
    if (!keyword) {
      const clearedDateArray = getDateArray(aYear.aYear, month.month);
      setDateArray(clearedDateArray);
      toast.warning(t('근로자명을 입력하세요'));
      return;
    }

    setLoading(true);
    // replace: true - push 시 검색마다 히스토리가 쌓여 back 버튼을 두 번 눌러야 하는 문제 방지
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
    const workerList = await getWorkerListAPI();

    if (workerList.length === 0) {
      setLoading(false);
      setWorkerCandidates([]);
      setSelectedCandidateKeys([]);
      toast.info(t('검색된 근로자가 없습니다.'));
      return;
    }

    if (workerList.length === 1) {
      setSelectedCandidateKeys([]);
      await onSelectWorker(workerList[0]);
      return;
    }

    const normalizedKeyword = normalizeName(keyword);
    const exactMatchedWorkers = workerList.filter((worker: any) => normalizeName(worker.wName || '') === normalizedKeyword);
    if (exactMatchedWorkers.length === 1) {
      setSelectedCandidateKeys([]);
      await onSelectWorker(exactMatchedWorkers[0]);
      return;
    }

    // 후보가 2명 이상이면 항상 근로자 선택 모달 표시
    setLoading(false);
    setWorkerCandidates(workerList);
    setSelectedCandidateKeys([]);
    setOpenModal((prev: any) => ({ ...prev, status: true, type: 'workerCandidateSelect' }));
  };

  const initiateSearchOptions = () => {
    setSjCd({ type: 'sjCd', sjCd: '', cdName: '' });
    setAYear({ type: 'aYear', aYear: toDay.substring(0, 4), cdName: toDay.substring(0, 4) });
    setMonth({ type: 'month', month: toDay.substring(4, 6), cdName: toDay.substring(4, 6) });
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    Object.keys(searchOption).map((el: any) => {
      return setSearchOption((prev: ISearchOption) => ({ ...prev, [el]: '', aYear: toDay.substring(0, 4), month: toDay.substring(4, 6) }));
    });
    setSelectedUser(null);
    setWorkerCandidates([]);
    setSelectedCandidateKeys([]);
    if (openModal.type === 'workerCandidateSelect') {
      setOpenModal((prev: any) => ({ ...prev, status: false, type: '' }));
    }
    initiateSearchOptions();
  };

  // 사진보기 체크박스 클릭
  const handleCheckboxChange = () => {
    setPhotoViewYn(!photoViewYn);
  };

  const onClickTab = (tab: string) => {
    setCurrentTabMrCd(tab);
  };

  const onClickDate = (dateParam: string) => {
    if (!selectedUser?.wCd) return;
    const newArrayObj: any = dateArray.find((el: any) => el.date === dateParam);

    if (newArrayObj) {
      // 해당날짜의 출근시간 또는 퇴근시간 데이터가 있을 때만 모달띄우기
      if (newArrayObj.aTimeMax || newArrayObj.aTimeMin) {
        const searchYmd = `${aYear.aYear}-${month.month}-${dateParam}`;
        const data = {
          partCd: '',
          partName: t('전체'),
          startDate: searchYmd,
          endDate: searchYmd,
          wCd: selectedUser.wCd,
          name: selectedUser.wName,
        };

        setOpenModal((prev: any) => ({ ...prev, status: true, type: 'attendDailyPersonalList', searchOption: data }));
      }
    }
  };

  const onClickFilter = () => {
    setOpenModal({ ...openModal, status: true, type: 'filter' });
  };

  const onClickFilterSearch = async () => {
    setOpenModal({ ...openModal, status: false, type: '' });
    await onClickSearch();
  };

  // 년/월 선택 핸들러
  const handleMonthSelect = useCallback(
    (year: string, m: string) => {
      setMonth({ type: 'month', month: m, cdName: m });

      const targetWCd = selectedUser?.wCd || searchOption.wCd;
      if (targetWCd) getMonthlyAttendListAPI(targetWCd, year, m);
      else toast.warn(t('근로자를 선택하세요'));

      setShowMonthPicker(false);
    },
    [selectedUser, month.month]
  );

  // 외부 클릭 시 팝업 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowMonthPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className='contents'>
      <div className='content-container oneColumn'>
        <Tab tabList={newTabList || tabInfo.tabList} currentTabMrCd={currentTabMrCd} setCurrentTabMrCd={setCurrentTabMrCd} onClickTab={onClickTab} size={size} />
        <Root loading={loading}>
          <SearchOptions>
            {size.innerSize.W > 1024 ? (
              <div className='inputsWrapper'>
                <div className='inputForm-row'>
                  <div className='inputForm-col'>
                    <Input
                      placeholder={t('근로자명')}
                      label=''
                      type='text'
                      id='wName'
                      name='wName'
                      state={searchOption}
                      setState={setSearchOption}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onClickSearch();
                      }}
                      clearable
                    />
                  </div>
                </div>
                <ShortcutButton icon='search' buttonText='검색' shortcut='' onClick={onClickSearch} />
                <InitBtn onClick={onClickInitiateSearchOption}>{t('초기화')}</InitBtn>
                <div className='flex-basic iconBtnGroup'>
                  <Toggle onChange={handleCheckboxChange} status={photoViewYn} text={t('사진보기')} />
                </div>
              </div>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                    <div className='inputForm-col'>
                      <input
                        type='search'
                        value={searchOption.wName}
                        onChange={(e) => setSearchOption((prev) => ({ ...prev, wName: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') onClickSearch();
                        }}
                        placeholder={t('근로자명')}
                        style={{ width: '100%', height: '2.5rem', padding: '0 0.75rem', background: 'var(--tonal)', borderRadius: '0.25rem', border: 'none', fontSize: '0.875rem' }}
                      />
                    </div>
                  </div>
                  <BtnGhost onClick={onClickFilter} className='filter-btn'>
                    <span className='material-symbols-rounded'>filter_alt</span>
                  </BtnGhost>
                </div>
              </div>
            )}
          </SearchOptions>
          <div ref={componentRef}>
            <h2 className='calendarMonthTitle flex-center'>
              <div className='arrow-button flex-center' role='button' tabIndex={0} onClick={() => handleMonthChange('prev')}>
                <span className='material-symbols-rounded'>arrow_back_ios</span>
              </div>
              <div className='month-title' role='presentation' onClick={() => setShowMonthPicker(true)}>
                {monthTitle}
              </div>
              <div className='arrow-button flex-center' role='button' tabIndex={0} onClick={() => handleMonthChange('next')}>
                <span className='material-symbols-rounded'>arrow_forward_ios</span>
              </div>
            </h2>
            {showMonthPicker && (
              <MonthPickerPopupStyle ref={datePickerRef}>
                <div className='year-month-selector'>
                  <div className='year-selector'>
                    <select value={aYear.aYear} onChange={(e) => setAYear({ type: 'aYear', aYear: e.target.value, cdName: e.target.value })}>
                      {YEAR_LIST.map(({ aYear: year }) => (
                        <option key={year} value={year}>
                          {year}
                          {t('년')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='month-grid'>
                    {MONTH_LIST.map(({ month: m }) => (
                      <button
                        key={m}
                        type='button'
                        className={month.month === m ? 'selected' : ''}
                        onClick={(e) => {
                          e.stopPropagation(); // 이벤트 버블링 방지
                          handleMonthSelect(aYear.aYear, m);
                        }}
                      >
                        {m}
                        {t('월')}
                      </button>
                    ))}
                  </div>
                </div>
              </MonthPickerPopupStyle>
            )}

            <div className='calendar'>
              {days.map((day, dayIndex) => (
                <div
                  className='days'
                  key={day}
                  style={{
                    color:
                      dayIndex === 0
                        ? theme === 'light'
                          ? 'var(--rose-500)'
                          : 'var(--rose-400)'
                        : dayIndex === 6
                        ? theme === 'light'
                          ? 'var(--blue-500)'
                          : 'var(--blue-400)'
                        : theme === 'light'
                        ? light.text_primary
                        : dark.text_primary,
                    borderLeft: dayIndex === 0 ? 'none' : '1px solid var(--outline)',
                  }}
                >
                  {day}
                </div>
              ))}
              {[...new Array(firstDayOfMonth)].map((e, i) => (
                <div className='emptyCells' key={`empty-${i}`} style={{ borderLeft: i === 0 ? 'none' : '1px solid var(--outline)' }} />
              ))}
              {dateArray.map(({ date, dayOfWeek, aTimeMin, aTimeMax, aImg }: any, index: number) => {
                const isTodayCell = `${aYear.aYear}${month.month}${date}` === toDay;
                return (
                  <div
                    key={index}
                    className='date'
                    onClick={() => onClickDate(date)}
                    role='presentation'
                    style={{
                      borderLeft: dayOfWeek === 0 ? 'none' : '1px solid var(--outline)',
                      overflow: 'hidden',
                      backgroundColor: isTodayCell ? 'var(--tonal-blue)' : 'transparent',
                    }}
                  >
                    <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <div
                        style={{
                          flex: size.innerSize.W <= 768 ? 'none' : 1,
                          ...(size.innerSize.W <= 768 ? { height: 'auto' } : { minHeight: 0, overflow: 'hidden' }),
                        }}
                      >
                        <div
                          className='dateNumber'
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            width: '100%',
                            ...(size.innerSize.W <= 1024 ? { padding: '0.125rem 0.25rem 0 0' } : {}),
                            color:
                              dayOfWeek === 0
                                ? theme === 'light'
                                  ? 'var(--rose-500)'
                                  : 'var(--rose-400)'
                                : dayOfWeek === 6
                                ? theme === 'light'
                                  ? 'var(--blue-500)'
                                  : 'var(--blue-400)'
                                : theme === 'light'
                                ? light.text_primary
                                : dark.text_primary,
                          }}
                        >
                          {date}
                        </div>
                        {photoViewYn ? (
                          aTimeMax !== '' ? (
                            aImg !== '' && aImg !== null ? (
                              size.innerSize.W > 768 ? (
                                <img style={{ display: !photoViewYn ? 'none' : 'block' }} src={aImg} alt='' width='100%' />
                              ) : null
                            ) : size.innerSize.W > 768 ? (
                              <div className='flex-center' style={{ paddingTop: '1rem' }}>
                                No Image
                              </div>
                            ) : null
                          ) : null
                        ) : null}
                      </div>
                      {photoViewYn && aTimeMax !== '' && aImg !== '' && aImg !== null && size.innerSize.W <= 768 && (
                        <img src={aImg} alt='' style={{ flex: 1, minHeight: 0, width: '100%', objectFit: 'cover' }} />
                      )}
                      {size.innerSize.W <= 768 ? (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            fontSize: '0.625rem',
                            lineHeight: '0.75rem',
                            gap: '0.125rem',
                            textAlign: 'center',
                            marginTop: !photoViewYn ? 'auto' : aTimeMax !== '' && aImg !== '' && aImg !== null ? '0.25rem' : 0,
                            color: theme === 'light' ? light.text_primary : dark.text_primary,
                          }}
                        >
                          {aTimeMin && aTimeMax && aTimeMin === aTimeMax ? (
                            <>
                              <div className={`timeBadge in ${!photoViewYn ? 'photoOff' : ''}`}>
                                <span className='material-symbols-rounded'>login</span>
                                {aTimeMin.slice(0, 5)}
                              </div>
                              <div className={`timeBadge out ${!photoViewYn ? 'photoOff' : ''}`} style={{ visibility: 'hidden' }} aria-hidden>
                                <span className='material-symbols-rounded'>logout</span>
                                {aTimeMax.slice(0, 5)}
                              </div>
                            </>
                          ) : (
                            <>
                              {aTimeMin && (
                                <div className={`timeBadge in ${!photoViewYn ? 'photoOff' : ''}`}>
                                  <span className='material-symbols-rounded'>login</span>
                                  {aTimeMin.slice(0, 5)}
                                </div>
                              )}
                              {aTimeMax && (
                                <div className={`timeBadge out ${!photoViewYn ? 'photoOff' : ''}`}>
                                  <span className='material-symbols-rounded'>logout</span>
                                  {aTimeMax.slice(0, 5)}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ) : aTimeMin && aTimeMax && aTimeMin === aTimeMax ? (
                        <div className='flex-between'>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <span className={`timeBadge in desktop ${!photoViewYn ? 'photoOff' : ''}`}>
                              <span className='material-symbols-rounded'>login</span>
                              {aTimeMin}
                            </span>
                          </div>
                          <div />
                        </div>
                      ) : (
                        <div className='flex-between' style={{ flexWrap: 'wrap', rowGap: '0.25rem' }}>
                          {aTimeMin ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <span className={`timeBadge in desktop ${!photoViewYn ? 'photoOff' : ''}`}>
                                <span className='material-symbols-rounded'>login</span>
                                {aTimeMin}
                              </span>
                            </div>
                          ) : (
                            <div />
                          )}
                          {aTimeMax ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <span className={`timeBadge out desktop ${!photoViewYn ? 'photoOff' : ''}`}>
                                <span className='material-symbols-rounded'>logout</span>
                                {aTimeMax}
                              </span>
                            </div>
                          ) : (
                            <div />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {remainingCells < 7 &&
                [...new Array(remainingCells)].map((e, i) => {
                  const colIndex = (firstDayOfMonth + dateArray.length + i) % 7;
                  return <div className='emptyCells' key={`empty-end-${i}`} style={{ borderLeft: colIndex === 0 ? 'none' : '1px solid var(--outline)' }} />;
                })}
            </div>
          </div>
        </Root>
      </div>
      {loading && (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'attendDailyPersonalList' && <AttendDailyPersonalListModal setOpenModal={setOpenModal} filter={openModal.searchOption} dGubun='c' />}
      </Portal>
      {isMobile() && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickFilterSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='filterWName'>{t('근로자명')}</label>
                  <div className='inputForm-col'>
                    <input
                      id='filterWName'
                      type='search'
                      value={searchOption.wName}
                      onChange={(e) => setSearchOption((prev) => ({ ...prev, wName: e.target.value }))}
                      placeholder={t('근로자명')}
                      style={{ width: '100%', height: '2.5rem', padding: '0 0.75rem', background: 'var(--tonal)', borderRadius: '0.25rem', border: 'none', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>
                {size.innerSize.W <= 1024 && (
                  <div className='filter-option-row'>
                    <label>{t('사진보기')}</label>
                    <div className='inputForm-col'>
                      <Toggle onChange={handleCheckboxChange} status={photoViewYn} text='' noBg />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'workerCandidateSelect' && (
          <GroupChkModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            title={t('근로자 선택')}
            type='unit'
            selectedProps={selectedCandidateKeys}
            setSelectedProps={setSelectedCandidateKeys}
            comparisonList={[]}
            isMultiSelect={false}
            maxLength={1}
            flatList={workerCandidates.map((worker: any) => ({
              key: worker.wCd,
              name: formatWorkerCandidateLabel(worker),
            }))}
          />
        )}
      </Portal>
    </div>
  );
};

export default Cattend5;
